export interface TabLinkOptions {
  id: any;
  domain?: string;
  verbose?: boolean;
  isInitiator?: boolean;
  useOnUnload?: boolean;

  // events
  onDisconnect?: (clean: boolean) => void;
  onConnect?: () => void;
  onData?: (data: any, event: Event) => void;
}

enum MessageType {
  DATA = 'data',
  DISCONNECT = 'disconnect',
  HANDSHAKE = 'hs',
  HANDSHAKE_ACK = 'hs_ack',
}

interface BaseMessageType {
  type: MessageType;
  integrationId: string;
}
interface DataMessageType extends BaseMessageType {
  data: any;
}

export default class TabLink {
  _window?: Window;
  _useOnUnload = true;

  // configurable?
  _verbose = false;

  isInitiator = false;

  _myId = '';
  get id() {
    return this._myId;
  }

  domain = '*';

  // events
  onConnect?: () => void;
  onDisconnect?: (isClean: boolean) => void;
  onData?: (data: any, event: Event) => void;

  _messageEventListener: (event: MessageEvent) => void = (event) => {
    const data: BaseMessageType = event.data;
    console.log('_messageEventListener data:', data);
    if (data.integrationId !== this.id) {
      console.warn('Invalid ID received; message discarded.', data);
      return;
    }

    switch (data.type) {
      case MessageType.HANDSHAKE:
        if (this.isInitiator) {
          const msg: BaseMessageType = {
            type: MessageType.HANDSHAKE_ACK,
            integrationId: this.id,
          };
          this._window?.postMessage(msg, this.domain);
          if (this.onConnect) this.onConnect();
        } else {
          console.error('Handshake sent to wrong side!');
        }
        break;
      case MessageType.HANDSHAKE_ACK:
        if (!this.isInitiator) {
          if (this.onConnect) {
            this.onConnect();
          }
        } else {
          console.error('Handshack ack sent to wrong side!');
        }
        break;
      case MessageType.DATA:
        if (this._verbose) console.log('received data');
        {
          const msg: DataMessageType = event.data;
          if (typeof this.onData === 'function') {
            this.onData(msg.data, event);
          }
        }
        break;
      case MessageType.DISCONNECT:
        console.log('Intercepted clean disconnect request');
        if (this.onDisconnect) {
          this.onDisconnect(true);
        }
        return;
    }
  };

  _unloadEvent = () => {
    this._window?.postMessage({ type: MessageType.DISCONNECT }, this.domain);
  };

  constructor(winId: any, options: TabLinkOptions) {
    // Check if window is anonymous function.
    const windowIsAnonymousFunction = typeof winId === 'function';

    // If it is not, we will immediately save the window ID
    if (!windowIsAnonymousFunction) {
      this._window = winId;
      if (this._verbose) console.log('Got window from value');
    }

    // Save options
    this._myId = options.id;
    this.isInitiator = options.isInitiator ?? false;
    this.domain = options.domain ?? '*';
    this._verbose = options.verbose ?? false;
    // event options
    this.onDisconnect = options.onDisconnect;
    this.onConnect = options.onConnect;
    this.onData = options.onData;
    this._useOnUnload = options.useOnUnload ?? true;

    window.addEventListener('message', this._messageEventListener); // onMessage

    if (this._useOnUnload) {
      window.addEventListener('beforeunload', this._unloadEvent); // onBeforeUnload
    }

    // if not initiator, immediately trigger handshake
    if (!this.isInitiator) {
      const msg: BaseMessageType = {
        type: MessageType.HANDSHAKE,
        integrationId: this.id,
      };
      this._window?.postMessage(msg, this.domain);
    }

    if (windowIsAnonymousFunction) {
      this._window = winId();
      if (this._verbose) console.log('Got window from anonymous function'); // note: cannot console.log this._window
    }
  } //constructor

  send(data: any) {
    const msg: DataMessageType = {
      type: MessageType.DATA,
      integrationId: this.id,
      data,
    };
    this._window?.postMessage(msg, this.domain);
  }

  disconnect() {
    const msg: BaseMessageType = {
      type: MessageType.DISCONNECT,
      integrationId: this.id,
    };
    this._window?.postMessage(msg, this.domain);
  }

  destroy() {
    window.removeEventListener('message', this._messageEventListener);
    if (this._useOnUnload) {
      window.removeEventListener('beforeunload', this._unloadEvent);
    }

    delete this.onConnect;
    delete this.onData;
    delete this.onDisconnect;
  }
} // class
